import { useManager } from 'dreamstate';
import { useCallback } from 'react';

import { Box, Flex, rem } from '@vimeo/bokeh';
import { Chats } from '@vimeo/bokeh-icons';
import { createInteractionDomName } from '@vimeo/cloud-live/libs/shared/utils/dom/identifying';
import { translations } from '@vimeo/cloud-live/translations';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Maybe } from '@vimeo/core/types/generic';
import { ChatHistory } from '@vimeo/live-core/components/interaction/chat/chat_history/ChatHistory';
import { ChatInput } from '@vimeo/live-core/components/interaction/chat/ChatInput';
import { ChatPreloader } from '@vimeo/live-core/components/interaction/chat/ChatPreloader';
import { EmptyStatePlaceholder } from '@vimeo/live-core/components/panels/empty';
import { useLiveGlobals } from '@vimeo/live-core/components/providers/globals';
import { InteractionFirebaseManager, IFirebaseContext } from '@vimeo/live-core/store/firebase/base';
import { ChatManager, IChatContext } from '@vimeo/live-core/store/firebase/interaction/chat';
import { trackEnableAudienceChat } from '@vimeo/live-core/tracking/tracking_engagement_chat';
import { EChatType } from '@vimeo/live-core/types/interaction_tools';
import { IInteractionStoreInitialState } from '@vimeo/live-core/types/store';

interface IChatManageProps extends IBaseDomElementProps {
  chatContext?: IChatContext;
  firebaseContext?: IFirebaseContext;
}

// todo: Unit test for content.
export function ChatManage({
  id = createInteractionDomName('chat-manage'),
  chatContext: {
    isEnabled,
    chatActions: { enableChat },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(ChatManager),
  firebaseContext: {
    interactionCredentials,
    connectionStatuses: { isInteractionConnected },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(InteractionFirebaseManager),
}: IChatManageProps) {
  const { initialState } = useLiveGlobals<IInteractionStoreInitialState>();

  const currentUserId: Maybe<string> = interactionCredentials?.user?.id;

  const onEnableChat = useCallback(() => {
    enableChat();
    trackEnableAudienceChat();
  }, [enableChat]);

  return (
    <Box id={id} flexDirection={'column'} flexGrow={1} maxHeight={'100%'} overflowY={'hidden'}>
      {isEnabled === null || !currentUserId ? (
        <ChatPreloader id={createInteractionDomName(id, 'preloader')} />
      ) : (
        <Flex
          flexDirection={'column'}
          flexGrow={1}
          height={'100%'}
          overflowY={'hidden'}
          padding={`0 ${rem(16)} ${rem(16)} ${rem(16)}`}
        >
          {initialState?.interaction?.isStandalone ? (
            isEnabled ? (
              <>
                <ChatHistory
                  chatType={EChatType.PUBLIC}
                  currentUserId={currentUserId}
                  scrollBackground={initialState?.interaction?.backgroundColor || 'background'}
                />
                <ChatInput chatType={EChatType.PUBLIC} isDisabled={!isInteractionConnected} />
              </>
            ) : (
              <EmptyStatePlaceholder
                icon={<Chats boxSize={'lg'} />}
                buttonLabel={translations.turnOn}
                description={translations.turnOnChatToEngageWithYourAudience}
                onButtonClick={onEnableChat}
              />
            )
          ) : (
            <>
              <ChatHistory
                chatType={EChatType.PUBLIC}
                currentUserId={currentUserId}
                scrollBackground={initialState?.interaction?.backgroundColor || 'background'}
              />

              <ChatInput chatType={EChatType.PUBLIC} isDisabled={!isInteractionConnected} />
            </>
          )}
        </Flex>
      )}
    </Box>
  );
}
